import { FC, MouseEvent, PropsWithChildren } from 'react';
import {
  styled,
  Theme,
  SxProps,
  Box,
  BoxProps,
  Breakpoint,
} from '@mui/material';

type Props = BoxProps & {
  justify?: 'flex-start' | 'center' | 'flex-end' | 'space-between';
  align?: 'flex-start' | 'center' | 'flex-end';
  centered?: boolean;
  breakpoint?: Breakpoint;
  spacing?: keyof Theme['spacingSizes'];
  className?: string;
  sx?: SxProps<Theme>;
  fullWidth?: boolean;
  reverse?: 'always' | 'breakpoint';
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
};

const shouldForwardProp = (prop: string) =>
  ![
    'fullWidth',
    'centered',
    'spacing',
    'justify',
    'align',
    'breakpoint',
    'reverse',
  ].includes(prop);

type StyledRowProps = Pick<
  Props,
  | 'centered'
  | 'breakpoint'
  | 'spacing'
  | 'justify'
  | 'align'
  | 'fullWidth'
  | 'reverse'
>;

const StyledRow = styled(Box, {
  shouldForwardProp,
})<StyledRowProps>(
  ({
    theme,
    centered,
    breakpoint,
    spacing,
    justify = 'flex-start',
    align,
    fullWidth,
    reverse,
  }) => ({
    display: 'flex',
    justifyContent: justify,
    alignItems: align ?? (centered ? 'center' : undefined),
    gap: spacing ? theme.spacing(theme.spacingSizes[spacing]) : 0,
    flexDirection: reverse === 'always' ? 'row-reverse' : 'row',
    width: fullWidth ? '100%' : undefined,

    [theme.breakpoints.down(breakpoint ?? 'xs')]: {
      flexDirection: reverse ? 'column-reverse' : 'column',
      alignItems: 'flex-start',

      '& > *': {
        width: '100%',
      },
    },
  }),
);

const Row: FC<PropsWithChildren<Props>> = ({
  children,
  spacing,
  className,
  fullWidth,
  onClick,
  ...props
}) => {
  return (
    <StyledRow
      className={className}
      spacing={spacing}
      fullWidth={fullWidth}
      onClick={onClick}
      {...props}
    >
      {children}
    </StyledRow>
  );
};

export default Row;
